/* Import React modules */
import React, { useState, useEffect, useRef } from "react";
/* Import other node modules */
import {
  FieldLabel,
  TextInput,
  InstructionText,
  Help,
  Field,
  Line,
  Form,
  Info,
  Icon,
  Radio,
  Select,
} from "@contentstack/venus-components";

import ContentstackAppSdk from "@contentstack/app-sdk";
import CryptoJS from "crypto-js";
import { CustomModal } from "./AddKey";
import WarningMessage from "../../components/WarningMessage";
/* Import our modules */
import localeTexts from "../../common/locale/en-us";
import utils from "../../common/utils";
import { TypeAppSdkConfigState } from "../../common/types";
/* Import node module CSS */
import "@contentstack/venus-components/build/main.css";
/* Import our CSS */
import "./styles.scss";
import services from "../../services";
import { getTokenFromUrl, setAxiosInterceptors } from "../../interceptor";

/* eslint-disable */
const ConfigScreen: React.FC = function () {
  const authTokenFromUrl = getTokenFromUrl();
  const appSdkDataRef = useRef<any>();
  // error tracking hooks
  const [state, setState] = useState<TypeAppSdkConfigState>({
    installationData: {
      configuration: {
        /* Add all your config fields here */
        /* The key defined here should match with the name attribute
        given in the DOM that is being returned at last in this component */
      },
      serverConfiguration: {
        baseUrl: "",
        apiKey: "",
      },
    },
    setInstallationData: (): any => {},
    appSdkInitialized: false,
  });

  const [isInvalidCreds, setIsInvalidCreds] = useState<boolean>(false);
  const { iterations }: any = localeTexts?.Decryption_Keys;
  const { keySize }: any = localeTexts?.Decryption_Keys;
  const password = `${process.env.REACT_APP_encryptWithAES}`;
  const [isCustom, setIsCustom] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [keyPathOptions, setKeyPathOptions] = useState<any[]>([]);

  const customKeyList = utils.keyOptions;
  const [customKeys, setCustomKeys] = useState(customKeyList);

  // by default selected keys in custom field
  const [formKeys, setformKeys] = useState<any[]>([
    { label: "_id", value: "_id" },
    { label: "title", value: "title" },
    { label: "name", value: "name" },
  ]);

  const encryptData = (msg: any, pass: any) => {
    const salt = CryptoJS?.lib?.WordArray?.random(128 / 8);
    const key = CryptoJS?.PBKDF2(pass, salt, {
      keySize: keySize / 32,
      iterations,
    });

    const iv = CryptoJS?.lib?.WordArray?.random(128 / 8);

    const encrypted = CryptoJS?.AES?.encrypt(msg, key, {
      iv,
      padding: CryptoJS?.pad?.Pkcs7,
      mode: CryptoJS?.mode?.CBC,
    });

    // salt, iv will be hex 32 in length
    // append them to the ciphertext for use  in decryption
    const transitmessage =
      salt?.toString() + iv?.toString() + encrypted?.toString();
    return transitmessage;
  };

  useEffect(() => {
    ContentstackAppSdk.init()
      .then(async (appSdk) => {
        const sdkConfigData = appSdk?.location?.AppConfigWidget?.installation;
        const apiKey = appSdk?.stack?.getData()?.api_key;
        setAxiosInterceptors(authTokenFromUrl, apiKey);
        if (sdkConfigData) {
          appSdkDataRef.current = sdkConfigData;
          const installationDataFromSDK =
            await sdkConfigData.getInstallationData();
          const setInstallationDataOfSDK = sdkConfigData.setInstallationData;
          setState({
            ...state,
            installationData: utils.mergeObjects(
              state.installationData,
              installationDataFromSDK
            ),
            setInstallationData: setInstallationDataOfSDK,
            appSdkInitialized: true,
          });

          setIsCustom(state?.installationData?.configuration?.iscustomFields);

          if (
            !state?.installationData?.configuration?.keypath_options?.length
          ) {
            const keyPathOptions: any = {
              target: {
                name: "keypath_options",
                value: customKeyList,
              },
            };
            updateConfig(keyPathOptions);
          } else {
            setCustomKeys(
              state?.installationData?.configuration?.keypath_options
            );
          }

          if (!state?.installationData?.configuration?.formKeys?.length) {
            const existingFormKeys: any = {
              target: {
                name: "formKeys",
                value: formKeys,
              },
            };
            updateConfig(existingFormKeys);
          } else {
            setformKeys(state?.installationData?.configuration?.formKeys);
          }
          checkSaveButtonValidation();
        }
      })
      .catch((error) => {
        console.error("appSdk initialization error", error);
      });
  }, []);

  const handleModalValue = (modalValueArr: any[], updatedValue: any[]) => {
    const updatedOptions: any = [...modalValueArr, ...updatedValue];

    const uniqueArray: any = [];
    const seenItems = new Set();
    updatedOptions?.forEach((item: any) => {
      const itemString = JSON.stringify(item);
      if (!seenItems?.has(itemString)) {
        seenItems.add(itemString);
        uniqueArray.push(item);
      }
    });

    const e: any = {
      target: {
        name: "keypath_options",
        value: [
          ...state?.installationData?.configuration?.keypath_options,
          ...uniqueArray,
        ],
      },
    };

    const formE: any = {
      target: {
        name: "formKeys",
        value: [
          ...state?.installationData?.configuration?.formKeys,
          ...uniqueArray,
        ],
      },
    };

    setCustomKeys((prevCustomKeys) => [...prevCustomKeys, ...uniqueArray]);
    setformKeys((prevKeys) => {
      return [...prevKeys, ...updatedValue];
    });

    setKeyPathOptions(updatedValue);
    updateConfig(e);
    updateConfig(formE);
  };

  /** updateConfig - Function where you should update the state variable
   * Call this function whenever any field value is changed in the DOM
   * */
  const updateConfig = async (e: any) => {
    // eslint-disable-next-line prefer-const
    let { name: fieldName, value: fieldValue } = e.target;

    const updatedConfig = state?.installationData?.configuration || {};
    const updatedServerConfig =
      state?.installationData?.serverConfiguration || {};

    if (fieldName === "apiKey" || fieldName === "baseUrl") {
      appSdkDataRef?.current?.setValidity(false);
      updatedServerConfig[fieldName] = fieldValue;
    } else {
      updatedConfig[fieldName] = fieldValue;
    }

    if (typeof state.setInstallationData !== "undefined") {
      await state.setInstallationData({
        ...state.installationData,
        configuration: updatedConfig,
        serverConfiguration: updatedServerConfig,
      });
    }

    if (fieldName === "baseUrl" || fieldName === "apiKey") {
      if (
        updatedServerConfig?.baseUrl?.length &&
        updatedServerConfig?.apiKey?.length
      ) {
        const encryptedBaseUrl = encryptData(
          updatedServerConfig?.baseUrl,
          password
        );
        const encryptedApiKey = encryptData(
          updatedServerConfig?.apiKey,
          password
        );
        const credsResponse = await services?.validateCreds(
          encryptedBaseUrl,
          encryptedApiKey
        );

        if (credsResponse?.error) {
          setIsInvalidCreds(true);
          appSdkDataRef?.current?.setValidity(false, {
            message: localeTexts?.configFields?.errors?.invalidCreds,
          });
        } else {
          const status = credsResponse?.data?.status;
          const data =
            typeof credsResponse?.data?.data === "string"
              ? JSON.parse(credsResponse?.data?.data)
              : credsResponse?.data?.data;

          if (status === 206 || status === 200 || data?.length) {
            setIsInvalidCreds(false);
            appSdkDataRef?.current?.setValidity(true);
          }
        }
      } else {
        setIsInvalidCreds(false);
        appSdkDataRef?.current?.setValidity(false, {
          message: localeTexts?.configFields?.errors?.emptyFields,
        });
      }
    }
    return true;
  };

  const checkSaveButtonValidation = () => {
    const baseUrl = state?.installationData?.serverConfiguration?.baseUrl;
    const apiKey = state?.installationData?.serverConfiguration?.apiKey;
    appSdkDataRef?.current?.setValidity(true);

    if (!baseUrl?.length && !apiKey?.length) {
      appSdkDataRef?.current?.setValidity(false, {
        message: localeTexts?.configFields?.errors?.emptyFields,
      });
    } else if (!baseUrl?.length) {
      appSdkDataRef?.current?.setValidity(false, {
        message: localeTexts?.configFields?.errors?.emptyBaseUrl,
      });
    } else if (!apiKey?.length) {
      appSdkDataRef?.current?.setValidity(false, {
        message: localeTexts?.configFields?.errors?.emptyApiKey,
      });
    }
  };

  const updateTypeObj = async (list: any[]) => {
    const formKeysTemp: any[] = [];
    list.forEach((key: any) => formKeysTemp.push(key?.value));
    setformKeys(list);
    const e: any = {};
    e.target = { name: "formKeys", value: list };
    updateConfig(e);
  };

  const updatecustomFields = (e: any) => {
    setIsCustom(e?.target?.id !== "allFields");
  };

  useEffect(() => {
    const e: any = {};
    e.target = { name: "iscustomFields", value: isCustom };
    updateConfig(e);
  }, [isCustom]);

  const closeModal = (type: any) => {
    setModalOpen(false);
  };

  const renderContent = () => {
    if (isCustom) {
      return (
        <div className="warning-container">
          <WarningMessage
            content={
              <>
                {
                  localeTexts?.configFields?.saveInEntry
                    ?.customFieldsInstruction
                }
                <a
                  href={localeTexts?.configFields?.saveInEntry?.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {localeTexts?.configFields?.saveInEntry?.link}
                </a>
                {
                  localeTexts?.configFields?.saveInEntry
                    ?.contentstackSupportText
                }
                <a
                  href={`mailto:${localeTexts?.configFields?.saveInEntry?.supportUrl}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {localeTexts?.configFields?.saveInEntry?.supportLink}
                </a>
                <br />
                <strong>
                  {localeTexts?.configFields?.saveInEntry?.NoteText}
                </strong>
              </>
            }
          />
        </div>
      );
    }
    return (
      <div className="warning-container">
        <WarningMessage
          content={
            <>
              {localeTexts?.configFields?.saveInEntry?.allFieldsInstruction}
              <a
                href={localeTexts?.configFields?.saveInEntry?.url}
                target="_blank"
                rel="noreferrer"
              >
                {localeTexts?.configFields?.saveInEntry?.link}
              </a>
              {localeTexts?.configFields?.saveInEntry?.contentstackSupportText}
              <a
                href={`mailto:${localeTexts?.configFields?.saveInEntry?.supportUrl}`}
                target="_blank"
                rel="noreferrer"
              >
                {localeTexts?.configFields?.saveInEntry?.supportLink}
              </a>
              <br />
              <strong>
                {localeTexts?.configFields?.saveInEntry?.NoteText}
              </strong>
            </>
          }
        />
      </div>
    );
  };

  return (
    <div className="layout-container">
      <div className="page-wrapper">
        {isInvalidCreds && (
          <div className="invalidCredsContainer" data-testid="errorComponent">
            <Info
              content={localeTexts?.configFields?.errors?.invalidCredentials}
              icon={<Icon icon="Error" />}
              type="warning"
              data-testid="errorComponent"
              className="invalidCredsComponent"
            />
          </div>
        )}
        <Form className="config-wrapper">
          <Field>
            <FieldLabel required htmlFor="baseUrl">
              {localeTexts.configFields.baseUrl.label}
            </FieldLabel>
            <Help text={localeTexts.configFields.baseUrl.help} />
            <TextInput
              id="baseUrl"
              required
              value={state.installationData.serverConfiguration.baseUrl}
              placeholder={localeTexts.configFields.baseUrl.placeholder}
              name="baseUrl"
              onChange={updateConfig}
              version="v2"
            />
            <InstructionText>
              {localeTexts.configFields.baseUrl.instruction}
            </InstructionText>
          </Field>
          <Line type="dashed" className="line" />
          <Field>
            <FieldLabel required htmlFor="apiKey">
              {localeTexts.configFields.apiKey.label}
            </FieldLabel>
            <Help text={localeTexts.configFields.apiKey.help} />
            <TextInput
              id="apiKey"
              required
              value={state.installationData.serverConfiguration.apiKey}
              placeholder={localeTexts.configFields.apiKey.placeholder}
              name="apiKey"
              onChange={updateConfig}
              version="v2"
            />
            <InstructionText className="Field Field--full">
              {localeTexts?.configFields?.apiKey?.instruction}
            </InstructionText>
          </Field>
          <div className="custom__fields_label">
            <FieldLabel required htmlFor="is_custom_json">
              {localeTexts?.configFields?.saveInEntry?.label}
            </FieldLabel>
            <Help text={localeTexts?.configFields?.saveInEntry?.help} />
          </div>
          <div className="Radio-wrapper Field Field--full">
            <Radio
              id="allFields"
              checked={!isCustom}
              required
              label={localeTexts?.configFields?.saveInEntry?.allFields}
              name="iscustomFields"
              value={false}
              onChange={updatecustomFields}
              data-testid="iscustomFields-radio"
            />
            <Radio
              id="customFields"
              checked={isCustom}
              required
              label={localeTexts?.configFields?.saveInEntry?.customFields}
              name="iscustomFields"
              value
              onChange={updatecustomFields}
            />
            <InstructionText>{renderContent()}</InstructionText>
          </div>

          {isCustom ? (
            <Field className="form-keys">
              <FieldLabel required htmlFor="form_keys">
                {localeTexts?.configFields?.keysField?.label}
              </FieldLabel>
              <Help text={localeTexts?.configFields?.keysField?.help} />

              <Select
                options={customKeys}
                onChange={updateTypeObj}
                value={formKeys}
                isMulti
                isSearchable
                version="v2"
                hasAddOption
                className="dam-keys-select"
                addOptionText={
                  <>
                    <Icon icon="Plus" />
                    {
                      localeTexts?.configFields?.customWholeJson?.modal
                        ?.addOption
                    }
                  </>
                }
                addOption={() => setModalOpen(true)}
              />
              {isModalOpen && (
                <CustomModal
                  keyPathOptions={keyPathOptions}
                  isOpen
                  onRequestClose={closeModal}
                  customOptions={customKeys}
                  handleModalValue={handleModalValue}
                />
              )}
            </Field>
          ) : (
            ""
          )}
        </Form>
      </div>
    </div>
  );
};

export default ConfigScreen;
